import styles from "@/styles/App.module.scss";
import ErrorBoundary from "@/components/app/ErrorBoundaryOutsideAuth.tsx";
import {
  createRouter,
  RouterProvider,
  useSearch,
} from "@tanstack/react-router";

import { routeTree } from "@/routeTree.gen";
import { useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { clearStoredCompany, getBuildingId } from "@/lib";
import { buildingScopedKey } from "@/lib/queryClient";
import {
  useQueries,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryOptions,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";

const NotFoundComponent = () => {
  const navigate = useNavigate();
  useEffect(() => {
    navigate({ to: "/404" });
  }, []);
  return null;
};

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  defaultOnCatch(error) {
    clearStoredCompany(getBuildingId());
    throw error;
  },
  defaultErrorComponent: () => (
    <ErrorBoundary className={styles.topLevelErrorBoundary} />
  ),
  scrollRestoration: true,
  scrollToTopSelectors: ["#page-scroll-container"],
  defaultNotFoundComponent: NotFoundComponent,
});

export const setBuildingId = (name: string) => {
  router.navigate({
    to: router.state.location.pathname,
    search: (old) => ({
      ...old,
      building: name,
    }),
  });
};

// The type definitions for the query wrappers are AI magic, but they seem to work so 🤷‍♂️.
export const useBuildingQuery = <
  TQueryFnData = unknown,
  TError = Error,
  TData = TQueryFnData,
  TQueryKey extends readonly unknown[] = readonly unknown[],
>(
  options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
): UseQueryResult<TData, TError> => {
  const search = useSearch({ from: "__root__" });
  const buildingId = search.building;

  return useQuery({
    ...options,
    queryKey: buildingScopedKey(
      buildingId,
      options.queryKey,
    ) as unknown as TQueryKey,
  });
};

// 1. Extract the exact UseQueryResult, capturing the queryFn return type explicitly
type ExtractQueryResult<T> =
  T extends UseQueryOptions<
    infer TQueryFnData,
    infer TError,
    infer TData,
    readonly unknown[]
  >
    ? UseQueryResult<
        // If TData is unknown (i.e., no 'select' option was used), fall back to the raw queryFn data
        unknown extends TData ? TQueryFnData : TData,
        // If TError is unknown, fall back to the standard Error type
        unknown extends TError ? Error : TError
      >
    : never;

// 2. Map over the tuple of queries to preserve strict typing for the whole array
type MappedQueryResults<T extends readonly unknown[]> = {
  -readonly [K in keyof T]: ExtractQueryResult<T[K]>;
};

export const useBuildingQueries = <
  // Constrain TQueries using strict 'unknown' bounds
  TQueries extends readonly UseQueryOptions<
    unknown,
    unknown,
    unknown,
    readonly unknown[]
  >[],
  TCombinedResult = MappedQueryResults<TQueries>,
>(options: {
  queries: readonly [...TQueries];
  combine?: (results: MappedQueryResults<TQueries>) => TCombinedResult;
}): TCombinedResult => {
  const search = useSearch({ from: "__root__" });
  const buildingId = search.building;

  return useQueries({
    ...options,
    queries: options.queries.map((query) => ({
      ...query,
      queryKey: buildingScopedKey(buildingId, query.queryKey),
    })),
    // Bridge our strict tuple type to TanStack's broader internal expectation
    combine: options.combine as unknown as
      | ((results: UseQueryResult<unknown, unknown>[]) => TCombinedResult)
      | undefined,
  }) as unknown as TCombinedResult;
};

export const useBuildingSuspenseQuery = <
  TQueryFnData = unknown,
  TError = Error,
  TData = TQueryFnData,
  TQueryKey extends readonly unknown[] = readonly unknown[],
>(
  options: UseSuspenseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
): UseSuspenseQueryResult<TData, TError> => {
  const search = useSearch({ from: "__root__" });
  const buildingId = search.building;

  return useSuspenseQuery({
    ...options,
    queryKey: buildingScopedKey(
      buildingId,
      options.queryKey,
    ) as unknown as TQueryKey,
  });
};

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
  interface StaticDataRouteOption {
    /**
     * The route sets `?building=` itself in beforeLoad, so BuildingUserProvider
     * re-runs the route instead of sending the user to the dashboard when it
     * finds no valid building.
     */
    resolvesOwnBuilding?: boolean;
  }
}

export default function RouterSetup() {
  return <RouterProvider router={router} />;
}
